import {
  CloudSyncOutlined,
  DeleteOutlined,
  ImportOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Col, Flex, Modal, Popconfirm, Row, TimePicker, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import {
  setTimeRangeDate,
  isTaskInShiftRange,
  getDisabledTimeForShift,
} from 'business/shift-report/report/pages/ReportEdit/components/new-task-drawer/services';
import pageStyle from 'business/shift-report/report/pages/ReportEdit/index.module.scss';
import { parseReportDateString } from 'business/shift-report/report/services/timeOperations';
import { parseShiftTimeStringWithReportDate } from 'business/shift-report/shift/services/timeOperations';
import { parseTaskDateString } from 'business/shift-report/task/services/timeOperations';
import {
  Module_Enum,
  ShiftReportChartViewFragment,
  TaskFullFragment,
  useDeleteNotValidatedTasksMutation,
  useShiftReportAutofillMutation,
  useShiftReportAutofillV2Mutation,
} from 'generated/graphql';
import errorReporting from 'technical/error-reporting';
import { TIME_HOUR_MINUTE_FORMAT } from 'technical/string/formatter';
import Button from 'ui/button';

interface Props {
  shiftReportId: string;
  previousTask?: TaskFullFragment;
  report: ShiftReportChartViewFragment;
  refreshReportData: () => void;
  autofillApiId: string | null;
  autofillOpcuaEnabled?: boolean;
}

const AutofillButton = ({
  shiftReportId,
  previousTask,
  report,
  refreshReportData,
  autofillApiId,
  autofillOpcuaEnabled,
}: Props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const [autofillV1, { loading: autofillV1Loading }] =
    useShiftReportAutofillMutation({ context: { headers: graphqlHeaders } });

  const [autofillV2, { loading: autofillV2Loading }] =
    useShiftReportAutofillV2Mutation({
      context: {
        headers: graphqlHeaders,
      },
    });

  const autofillLoading = autofillV1Loading || autofillV2Loading;
  const [deleteAutofillTasks, { loading: deleteLoading }] =
    useDeleteNotValidatedTasksMutation({
      variables: { shiftReportId },
      context: {
        headers: graphqlHeaders,
      },
    });

  const handleOpen = () => {
    const prefillStartDate = previousTask
      ? parseTaskDateString(previousTask.endDate)
      : parseShiftTimeStringWithReportDate(
          report.shift.startTime,
          parseReportDateString(report.date),
        );
    const prefillEndDate = dayjs();
    setStartTime(prefillStartDate);
    setEndTime(prefillEndDate);
    setVisible(true);
  };

  const handleClose = () => setVisible(false);

  const checkCriticalOverlap = (
    startDate: Dayjs,
    endDate: Dayjs,
    tasks: TaskFullFragment[],
  ) => {
    return tasks.some(
      (existingTask) =>
        (startDate.isSameOrAfter(existingTask.startDate) &&
          startDate.isBefore(existingTask.endDate)) ||
        (endDate.isAfter(existingTask.startDate) &&
          endDate.isSameOrBefore(existingTask.endDate)) ||
        (startDate.isSameOrBefore(existingTask.startDate) &&
          endDate.isSameOrAfter(existingTask.endDate)),
    );
  };

  const handleAutofillV1 = async (startDate: Dayjs, endDate: Dayjs) => {
    try {
      const { data: autofillData } = await autofillV1({
        variables: {
          shiftReportId,
          startDate,
          endDate,
        },
      });

      if (!autofillData) {
        throw new Error('autofill-mutation-no-response');
      }

      const {
        shiftReportAutofill: {
          success,
          count,
          message: autofillMutationMessage,
        },
      } = autofillData;

      if (!success) {
        throw new Error(
          autofillMutationMessage || 'autofill-mutation-no-message',
        );
      }

      message.info(
        t('pages.report.chart.autofill.result', {
          count,
          context: count === 0 ? 'noTask' : undefined,
        }),
      );
      refreshReportData();
    } catch (e) {
      message.error(t('pages.report.chart.autofill.errors.mutation'));
      if (e instanceof Error) {
        errorReporting.warning(e);
      }
    }
  };

  const handleAutofillV2 = async (startDate: Dayjs, endDate: Dayjs) => {
    try {
      const { data: autofillData } = await autofillV2({
        variables: {
          shiftReportId,
          startDate,
          endDate,
        },
      });

      if (!autofillData) {
        throw new Error('autofill-mutation-no-response');
      }

      const {
        shiftReportAutofillV2: { count },
      } = autofillData;

      message.info(
        t('pages.report.chart.autofill.result', {
          count,
          context: count === 0 ? 'noTask' : undefined,
        }),
      );
      refreshReportData();
    } catch (e) {
      message.error(t('pages.report.chart.autofill.errors.mutation'));
      if (e instanceof Error) {
        errorReporting.warning(e);
      }
    }
  };

  const handleAutofillTasks = async () => {
    if (!report) {
      return;
    }
    if (!startTime || !endTime) {
      message.warning(t('pages.report.chart.autofill.errors.notInRange'));
      return;
    }
    const [startDate, endDate] = setTimeRangeDate(
      parseReportDateString(report.date),
      report.shift,
    )(startTime.clone(), endTime.clone());
    if (
      !startDate ||
      !endDate ||
      !isTaskInShiftRange(parseReportDateString(report.date), report.shift)(
        startDate,
        endDate,
      )
    ) {
      message.warning(t('pages.report.chart.autofill.errors.notInRange'));
      return;
    }

    if (
      checkCriticalOverlap(
        startDate,
        endDate,
        report.tasks.filter(
          ({ activity: { type } }) => type === 'excavation' || type === 'build',
        ),
      )
    ) {
      message.warning(t('pages.report.chart.autofill.errors.overlap'));
      return;
    }

    handleClose();

    if (autofillOpcuaEnabled) {
      return handleAutofillV2(startDate, endDate);
    }

    if (autofillApiId) {
      return handleAutofillV1(startDate, endDate);
    }

    // Should never happend because the button is available only if
    // autofillOpucaEnabled or autofillApiId is defined but,
    // in case of, better to have it
    message.error(t('pages.report.chart.autofill.errors.mutation'));
  };

  const handleDeleteAllAutofillTasks = () =>
    deleteAutofillTasks()
      .then(() => refreshReportData())
      .then(() => handleClose())
      .catch((error) => {
        errorReporting.warning(error);
        message.error(t('errors.deletion_failed'));
      });

  return (
    <>
      <Button
        className={pageStyle.taskCta}
        onClick={handleOpen}
        loading={autofillLoading}
        icon={<ImportOutlined />}
      >
        {t('pages.report.chart.autofill.cta')}
      </Button>
      <Modal
        title={t('pages.report.chart.autofill.modalTitle')}
        open={visible}
        onCancel={handleClose}
        footer={null}
      >
        <Flex vertical gap={8}>
          <Row>
            <Col span={11}>
              <TimePicker
                value={startTime}
                onChange={(value) => setStartTime(value)}
                needConfirm={false}
                hideDisabledOptions
                format={TIME_HOUR_MINUTE_FORMAT}
                disabledTime={() =>
                  getDisabledTimeForShift(
                    report.shift,
                    parseReportDateString(report.date),
                  )
                }
                style={{ width: '100%' }}
                placeholder={t('pages.form.startTime.placeholder')}
              />
            </Col>
            <Col span={11} offset={2}>
              <TimePicker
                value={endTime}
                onChange={(value) => setEndTime(value)}
                needConfirm={false}
                hideDisabledOptions
                format={TIME_HOUR_MINUTE_FORMAT}
                disabledTime={() =>
                  getDisabledTimeForShift(
                    report.shift,
                    parseReportDateString(report.date),
                  )
                }
                style={{ width: '100%' }}
                placeholder={t('pages.form.endTime.placeholder')}
              />
            </Col>
          </Row>
          <Button
            type="primary"
            style={{ width: '100%' }}
            onClick={handleAutofillTasks}
            icon={<CloudSyncOutlined />}
          >
            {t('pages.report.chart.autofill.cta')}
          </Button>
          <Popconfirm
            title={t('pages.report.chart.autofill.deletePopconfirm')}
            okButtonProps={{ danger: true }}
            onConfirm={handleDeleteAllAutofillTasks}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
          >
            <Button
              type="default"
              style={{
                width: '100%',
                backgroundColor: '#eca52b',
                color: '#fff',
              }}
              loading={deleteLoading}
              icon={<DeleteOutlined />}
            >
              {t('pages.report.chart.autofill.deleteCta')}
            </Button>
          </Popconfirm>
        </Flex>
      </Modal>
    </>
  );
};

export default AutofillButton;
